import React, { useEffect, useState } from 'react';
import { InputInfo } from '../../../models/APIModels';
import { cloneDeep } from 'lodash';
import { Grid, Box, TextField, FormControlLabel, Switch } from '@mui/material';
import AutoComplete from '../../AutoComplete/AutoCompleteSelect';
// import { dataTypes } from '../AIEngineConfiguration';
import { useDebounce } from 'use-debounce';
import clientUtils from '../../../Utils/clientUtils.ts';

interface IProps {
  selectedInput: InputInfo;
  inputs: InputInfo[];
  updateInputConfiguration: (inputs: InputInfo[], selectedInput: InputInfo) => void;
  dataTypes: [];
  disabled?: boolean;
}

const InputConfiguration = ({
  selectedInput,
  inputs,
  updateInputConfiguration,
  configureVariableDisplayOptions,
  dataTypes,
  disabled = false,
}) => {
  // ➡️ State Variables .....
  const [selectedType, setSelectedType] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [selectedInputDescription, setSelectedInputDescription] = useState('');
  const [multilineToggleBtn, setMultilineToggleBtn] = useState(false);
  const [previewImageToggleBtn, setPreviewImageToggleBtn] = useState(false);

  const [isInputAlreadyExits, setIsInputAlreadyExits] = useState(false);
  const [debouncedValue] = useDebounce(selectedName, 500);

  // ➡️ Functions/Methods .....

  // ⚒️ Function/Method :: On Input Text Change Handler .....
  const onInputTextChange = (event, field) => {
    const value = event.target.value;
    const cloneInputs = cloneDeep(inputs);
    const index = cloneInputs.findIndex((i) => i.name === selectedInput.name);
    // console.log('index', index);
    if (index !== -1) {
      const input = cloneInputs[index];
      if (field === 'name') {
        const cloneDisplaySettings = configureVariableDisplayOptions('view');
        const keyName = clientUtils.uniqueKeyNameGenForVariableDisplayOptions(input[field], 'inputcon');
        let DisplaySettingsFlagValue = { enableMultiLineText: false, enablePictureViewer: false };
        if (keyName in cloneDisplaySettings) {
          DisplaySettingsFlagValue = cloneDisplaySettings[keyName];
          configureVariableDisplayOptions('pop', { name: input[field] });
          input[field] = value;

          configureVariableDisplayOptions('push', { name: input[field] });
          if (DisplaySettingsFlagValue.enableMultiLineText) {
            configureVariableDisplayOptions('setflag', { name: input[field] },'multiline');
          }
          if (DisplaySettingsFlagValue.enablePictureViewer) {
            configureVariableDisplayOptions('setflag', { name: input[field] },'imgPreview');
          }
        } else {
          input[field] = value;
        }
        setSelectedName(value);
      } else if (field === 'inputDescription') {
        input[field] = value;
        setSelectedInputDescription(value);
      }
      updateInputConfiguration(cloneInputs, input);
    }
  };

  // ⚒️ Function/Method :: Handels Input Type of Inputs .....
  const handleInputType = (value) => {
    // console.log(value)
    const cloneInputs = cloneDeep(inputs);
    if (selectedInput) {
      const index = cloneInputs.findIndex((i) => i.name === selectedInput.name);
      if (index !== -1) {
        const input = cloneInputs[index];
        input.inputType = value.name;
        updateInputConfiguration(cloneInputs, input);
      }
    }
  };

  // ➡️ Hooks .....

  // 🪝 perform side effects when 'selectedInput' changes .....
  useEffect(() => {
    // If 'selectedInput' has a value:
    if (selectedInput) {
      // console.log('Input Selected ::=>', selectedInput);
      // Update state variables based on 'selectedInput' properties:
      setSelectedName(selectedInput.name); // Set the selected name
      setSelectedInputDescription(selectedInput.inputDescription); // Set the input description

      // Find the corresponding inputType object in the 'dataTypes' array:
      const inputType = dataTypes.find((i) => i.name === selectedInput.inputType);

      // If a matching inputType is found, set the selected type:
      if (inputType) {
        setSelectedType(inputType);
      } else {
        // If no matching inputType is found, set the selected type to an empty string:
        setSelectedType('');
      }

      // prefill Multiline Display .....
      const variableDisplayClone = configureVariableDisplayOptions('view');
      const displayKeyName = clientUtils.uniqueKeyNameGenForVariableDisplayOptions(selectedInput.name, 'inputcon');
      const enableMultiLineText = variableDisplayClone[displayKeyName]?.enableMultiLineText ?? false;
      const enablePictureViewer = variableDisplayClone[displayKeyName]?.enablePictureViewer ?? false;
      setMultilineToggleBtn(enableMultiLineText);
      setPreviewImageToggleBtn(enablePictureViewer);
    }
  }, [selectedInput]); // Only run this effect when 'selectedInput' changes

  useEffect(() => {
    if (selectedInput) {
      if (multilineToggleBtn) {
        configureVariableDisplayOptions('push', selectedInput);
        configureVariableDisplayOptions('setflag', selectedInput,'multiline');
      } else {
        setPreviewImageToggleBtn(false)
        configureVariableDisplayOptions('setflagFalse', selectedInput);
      }
    }
  }, [multilineToggleBtn]);


  useEffect(() => {
    if (selectedInput) {
      if (previewImageToggleBtn) {
        configureVariableDisplayOptions('push', selectedInput);
        configureVariableDisplayOptions('setflag', selectedInput,'imgPreview');
      } else {
        setMultilineToggleBtn(false)
        configureVariableDisplayOptions('setflagFalseImgPrev', selectedInput);
      }
    }
  }, [previewImageToggleBtn]);

  //➡️ Debug Code's .....

  // useEffect(() => {
  //   console.log("Inputs:")
  //   console.log(inputs)
  // }, [inputs])

  return (
    <Grid container spacing={1} direction="row">
      <Grid item xs={12}>
        <Box className="hedading">
          <div className="title">Input Configuration</div>
          <div className="line"></div>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="name"
          label="Name"
          variant="outlined"
          placeholder=""
          size="small"
          fullWidth
          onChange={(e) => onInputTextChange(e, 'name')}
          value={selectedName}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="inputDescription"
          label="Input Description"
          variant="outlined"
          placeholder=""
          size="small"
          fullWidth
          onChange={(e) => onInputTextChange(e, 'inputDescription')}
          value={selectedInputDescription}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <AutoComplete
          id="input-type-select"
          items={dataTypes}
          itemText="name"
          itemKey="id"
          labelText="Input Type"
          placeHolderText="Select Input Type"
          multiSelect={false}
          onChange={handleInputType}
          defaultValue={selectedType ?? ''}
          disabled={disabled}
        />
      </Grid>

      {/* Multiiline Toggle */}
      {selectedType?.name == 'String' && !disabled && (
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <FormControlLabel
            sx={{ ml: '2px' }}
            control={
              <div style={{ display: 'flex', marginLeft: '5px' }}>
                <Switch size="small" checked={multilineToggleBtn} color={'info'} />
              </div>
            }
            onChange={(e) => {
              setMultilineToggleBtn(e.target.checked);
            }}
            label="Enable multi-line"
            labelPlacement="start"
          />
        </Grid>
      )}

      {/* enablePictureViewer Toggle */}
      {selectedType?.name == 'File' && !disabled && (
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <FormControlLabel
            sx={{ ml: '2px' }}
            control={
              <div style={{ display: 'flex', marginLeft: '5px' }}>
                <Switch size="small" checked={previewImageToggleBtn} color={'info'} />
              </div>
            }
            onChange={(e) => {
              setPreviewImageToggleBtn(e.target.checked);
            }}
            label="Enable Picture Preview"
            labelPlacement="start"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default InputConfiguration;
